<template>
  <div class="border border-neutral-300 rounded-md overflow-hidden bg-white">
    <!-- Header - Clickable to expand/collapse -->
    <button
      class="w-full px-4 py-3 bg-blue-50 border-b border-blue-200 hover:bg-blue-100 transition-colors duration-200 text-left cursor-pointer"
      @click="toggleExpanded"
    >
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-2">
          <svg
            class="w-4 h-4 text-blue-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          <span class="font-medium text-blue-800">{{
            t('summary.title')
          }}</span>
          <span
            v-if="messageStore.summaryTimestamp"
            class="text-xs text-blue-600"
          >
            {{ formatTime(messageStore.summaryTimestamp) }}
          </span>
        </div>
        <div class="flex items-center gap-2">
          <!-- Copy Button -->
          <button
            v-if="messageStore.currentSummary"
            class="p-1 text-blue-600 hover:text-blue-800 hover:bg-blue-100 rounded transition-colors duration-200"
            :title="copyButtonText"
            @click.stop="copySummary"
          >
            <svg
              v-if="!isCopied"
              class="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
              />
            </svg>
            <svg
              v-else
              class="w-4 h-4 text-green-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M5 13l4 4L19 7"
              />
            </svg>
          </button>

          <!-- Expand/Collapse Chevron -->
          <svg
            class="w-4 h-4 text-blue-600 transition-transform duration-200"
            :class="{ 'rotate-180': isExpanded }"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </div>
      </div>
    </button>

    <!-- Collapsible Content -->
    <div v-show="isExpanded" class="transition-all duration-300 ease-in-out">
      <!-- Loading State -->
      <div v-if="messageStore.isSummaryGenerating" class="p-4 bg-blue-25">
        <div class="flex items-center gap-3">
          <span
            class="inline-flex h-4 w-4 animate-spin rounded-full border-2 border-blue-500 border-t-transparent"
          ></span>
          <p class="text-sm text-blue-700">{{ t('summary.generating') }}</p>
        </div>
      </div>

      <!-- Error State -->
      <div v-else-if="messageStore.summaryError" class="p-4 bg-red-50">
        <div class="flex items-start gap-3">
          <svg
            class="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fill-rule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
              clip-rule="evenodd"
            />
          </svg>
          <div>
            <p class="font-medium text-red-800 text-sm">
              {{ t('summary.error') }}
            </p>
            <p class="text-sm text-red-700 mt-1">
              {{ messageStore.summaryError }}
            </p>
            <button
              class="mt-2 text-sm text-red-600 hover:text-red-800 underline"
              @click="messageStore.clearSummary()"
            >
              {{ t('summary.close') }}
            </button>
          </div>
        </div>
      </div>

      <!-- Summary Content -->
      <div v-else-if="messageStore.currentSummary" class="p-4">
        <div class="prose prose-sm max-w-none">
          <div
            class="text-neutral-700 leading-relaxed markdown-content"
            v-html="renderedMarkdown"
          ></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMessageStore } from '@/stores/messages';
import { renderMarkdown, markdownToPlainText } from '@/utils/markdown';

const { t, locale } = useI18n();
const messageStore = useMessageStore();
const isExpanded = ref(false);
const isCopied = ref(false);

// Computed property to render markdown
const renderedMarkdown = computed(() =>
  renderMarkdown(messageStore.currentSummary || '')
);

// Auto-expand when summary starts generating or when there's an error
watch(
  () => messageStore.isSummaryGenerating || messageStore.summaryError,
  (newValue) => {
    if (newValue) {
      isExpanded.value = true;
    }
  }
);

// Auto-expand when summary is received
watch(
  () => messageStore.currentSummary,
  (newSummary) => {
    if (newSummary) {
      isExpanded.value = true;
    }
  }
);

const toggleExpanded = () => {
  isExpanded.value = !isExpanded.value;
};

const formatTime = (timestamp: string) => {
  const date = new Date(timestamp);
  if (isNaN(date.getTime())) return '';
  // Use the agent locale for date formatting; map our ISO codes to BCP-47.
  const localeTag = locale.value === 'de' ? 'de-DE' : 'nl-NL';
  return date.toLocaleTimeString(localeTag, {
    hour: '2-digit',
    minute: '2-digit',
  });
};

const copyButtonText = computed(() => {
  return isCopied.value ? t('summary.copied') : t('summary.copy');
});

const copySummary = async () => {
  if (!messageStore.currentSummary) return;

  try {
    // Convert markdown to plain text for SAP Sales Cloud plain text fields
    const plainText = markdownToPlainText(messageStore.currentSummary);
    await navigator.clipboard.writeText(plainText);
    isCopied.value = true;

    // Reset the copied state after 2 seconds
    setTimeout(() => {
      isCopied.value = false;
    }, 2000);
  } catch (error) {
    console.error('Failed to copy summary to clipboard:', error);
  }
};
</script>
